import { backOperatorValuesToAql } from '../utils/operatorValuesToAql'
import { mapAllFiltersToAQLFields } from '../utils/mapFiltersToAQLFields'
import { regexExecAll } from '../utils/regexExecAll'
import { regexpCaptureGroupsToSearchOperatorConfig } from '../utils/searchOperatorConfig'
import { replaceFirst } from '../utils/replaceFirst'
import { targetedKeyLowHighFieldPattern } from '../../regexp'
import type { AllDashboardCardFilters, SearchOperatorConfig } from '../../types'
import type { LowHighOperator } from '../../enums'
import type { LowHighOperatorFilterKey } from '../utils/mapFiltersToAQLFields'

export const parseLowHighOperatorField = (
    aql: string,
    searchKey: LowHighOperatorFilterKey,
): Partial<AllDashboardCardFilters> => {
    const emptyConfig = { [searchKey]: '', [`${searchKey}Low`]: '', [`${searchKey}High`]: '' }

    if (!aql) {
        return emptyConfig
    }

    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []

    if (!aqlFields.length) {
        return {}
    }

    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const allMatches = (aqlFields as unknown as string[]).reduce<RegExpExecArray[]>((acc, field) => {
        if (acc.length) {
            return acc
        }

        const pattern = targetedKeyLowHighFieldPattern(field)
        const regex = new RegExp(pattern, 'gi')
        const maybeMatches = regexExecAll(aql, regex)

        return maybeMatches?.length ? maybeMatches : []
    }, [])

    if (!allMatches.length) {
        return emptyConfig
    }

    const firstConfig = allMatches.reduce<SearchOperatorConfig | null>((acc, match) => {
        if (acc) {
            return acc
        }

        const config = regexpCaptureGroupsToSearchOperatorConfig(match.groups)

        if (!config.operator || !config.lowValue) {
            return null
        }

        return config
    }, null)

    if (!firstConfig) {
        return emptyConfig
    }

    const { operator, lowValue, highValue } = firstConfig

    return {
        [searchKey]: operator,
        [`${searchKey}Low`]: lowValue,
        [`${searchKey}High`]: highValue,
    }
}

export const updateLowHighOperatorField = (
    prior: string,
    searchKey: LowHighOperatorFilterKey,
    operator: LowHighOperator | '' = '',
    lowValue = '',
    highValue = '',
): string => {
    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []
    const valueWithOperator = backOperatorValuesToAql({ operator, lowValue, highValue })
    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const maybeReplaced = (aqlFields as unknown as string[]).reduce<string | null>((acc, field, index) => {
        if (acc) {
            // If multiple aqlFields, only match the first e.g. ['foo', 'bar']
            return acc
        }

        const lastIndex = index === aqlFields.length - 1
        const regex = new RegExp(targetedKeyLowHighFieldPattern(field), 'gi')
        const replacement = valueWithOperator ? `${field}:${valueWithOperator}` : ''
        const replaced = replaceFirst(prior, regex, replacement, {
            removeOtherMatches: true,
            handleNotFound: lastIndex ? 'append' : undefined,
        })

        if (replaced === prior) {
            // Not found
            return null
        }

        return replaced
    }, null)

    return maybeReplaced || prior
}
